import React, { ChangeEvent, useCallback, useRef, useState } from 'react';

import {
  Button,
  buttonClasses,
  ClickAwayListener,
  FormControl,
  Grow,
  inputClasses,
  InputLabel,
  inputLabelClasses,
  MenuItem,
  menuItemClasses,
  MenuList,
  NativeSelect,
  nativeSelectClasses,
  Paper,
  Popper,
  styled,
} from '@mui/material';

import { CheckmarkIcon, SelectArrowDownNewIcon } from '../../../assets/icons';

export type TextSelectProps = {
  id?: string;
  name?: string;
  label?: string;
  activeIndex: number;
  onChange: (newIndex: number) => void;
  items: {
    name: string;
    hide?: boolean;
  }[];
  className?: string;
  hideBorderBottom?: boolean;
};

export const TextSelect = ({
  id,
  name,
  label,
  activeIndex,
  onChange,
  items,
  className,
  hideBorderBottom = false,
}: TextSelectProps) => {
  const [desktopDropdownOpen, setDesktopDropdownOpen] = useState(false);
  const dropdownButton = useRef(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const onNativeSelectChange = useCallback(
    (evt: ChangeEvent<HTMLSelectElement>) => {
      onChange(parseInt(evt.target.value));
    },
    [onChange],
  );

  const toggleDesktopDropdown = useCallback(() => {
    setDesktopDropdownOpen(!desktopDropdownOpen);
  }, [desktopDropdownOpen, setDesktopDropdownOpen]);

  const closeDesktopDropdown = useCallback(() => {
    setDesktopDropdownOpen(false);
  }, [setDesktopDropdownOpen]);

  const selectItem = useCallback(
    (idx: number) => {
      setDesktopDropdownOpen(false);
      onChange(idx);
    },
    [onChange, setDesktopDropdownOpen],
  );

  const desktopId = id ? `${id}-desktop` : undefined;
  const mobileId = id ? `${id}-mobile` : undefined;

  return (
    <Container className={className} ref={containerRef}>
      <DesktopTextDropdown>
        <Button
          ref={dropdownButton}
          id={desktopId}
          onClick={toggleDesktopDropdown}
          endIcon={<SelectArrowDownNewIcon />}
          className={desktopDropdownOpen ? 'menu-open' : undefined}
          style={{
            overflow: 'hidden',
            whiteSpace: 'nowrap',
          }}
        >
          {label ? <InputLabel htmlFor={desktopId}>{label}</InputLabel> : null}
          <span
            style={{
              marginLeft: label ? '18px' : 0,
              fontWeight: label
                ? 'var(--font-weight-regular)'
                : 'var(--font-weight-medium)',
            }}
          >
            {items[activeIndex]?.name}
          </span>
        </Button>
        <DesktopDropdownPopper
          open={desktopDropdownOpen}
          anchorEl={dropdownButton.current}
          role={undefined}
          placement="bottom-start"
          transition
          disablePortal
        >
          {({ TransitionProps }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin: 'center top',
                inset: 'unset !important',
              }}
            >
              <DesktopDropdown>
                <ClickAwayListener onClickAway={closeDesktopDropdown}>
                  <DesktopDropdownMenuList
                    autoFocusItem={desktopDropdownOpen}
                    id={name}
                    aria-labelledby={desktopId}
                  >
                    {items.map((item, idx) => (
                      <DesktopDropdownMenuItem
                        disableRipple
                        key={item.name}
                        className={item.hide ? 'hidden' : ''}
                        onClick={() => selectItem(idx)}
                        style={{ paddingLeft: '40px', paddingRight: '40px' }}
                        autoFocus={activeIndex === idx}
                      >
                        {item.name}
                        {activeIndex === idx && (
                          <CheckmarkIcon
                            style={{ color: 'var(--color-light-red)' }}
                          />
                        )}
                      </DesktopDropdownMenuItem>
                    ))}
                  </DesktopDropdownMenuList>
                </ClickAwayListener>
              </DesktopDropdown>
            </Grow>
          )}
        </DesktopDropdownPopper>
      </DesktopTextDropdown>
      <NativeMobileTextDropdown
        className="native-dropdown"
        hideBorderBottom={hideBorderBottom}
      >
        {label ? (
          <InputLabel
            htmlFor={mobileId}
            sx={{
              pointerEvents: 'none',
            }}
          >
            {label}
          </InputLabel>
        ) : null}
        <NativeSelect
          inputProps={{
            name: name,
            id: id,
            style: {
              paddingTop: '30px',
              paddingRight: '42px',
              marginTop: '-26px',
              fontWeight: label
                ? 'var(--font-weight-regular)'
                : 'var(--font-weight-medium)',
              display: 'inline',
            },
          }}
          value={activeIndex}
          onChange={onNativeSelectChange}
          disableUnderline
          IconComponent={SelectArrowDownNewIcon}
        >
          {items.map((item, itemIdx) => (
            <option key={itemIdx} value={itemIdx} hidden={item.hide ?? false}>
              {item.name}
            </option>
          ))}
        </NativeSelect>
      </NativeMobileTextDropdown>
    </Container>
  );
};

export default TextSelect;

const Container = styled('div')({
  display: 'inline-flex',
  flexDirection: 'column',
  justifyContent: 'center',
  width: '100%',
  height: '100%',
});

// -----------------------------------------------------------------------------
// Custom Select for Desktop
// -----------------------------------------------------------------------------

const DesktopTextDropdown = styled(FormControl)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  position: 'relative',

  [`.${inputLabelClasses.root}`]: {
    ...theme.typography.body,
    fontWeight: theme.typography.fontWeightMedium,
    color: 'var(--color-dark-coal)',
    position: 'relative',
    display: 'block',
    transform: 'none',

    [`&.${inputLabelClasses.focused}`]: {
      color: 'var(--color-dark-coal)',
    },
  },

  [`.${buttonClasses.root}.${buttonClasses.text}`]: {
    ...theme.typography.body,
    fontWeight: theme.typography.fontWeightRegular,
    color: 'var(--color-dark-coal)',

    // 5px padding from button
    paddingTop: 0, // 5px in total
    paddingBottom: '10px', // 15px in total

    [theme.breakpoints.up('sm')]: {
      // 3.5px padding from button
      paddingTop: '5px', // 8.5px in total
      paddingBottom: '17px', // 20.5px in total
    },

    [theme.breakpoints.up('md')]: {
      // 3.5px padding from button
      paddingTop: '10.5px', // 14px in total
      paddingBottom: '25.5px', // 29px in total
    },

    [theme.breakpoints.up('lg')]: {
      // 2px padding from button
      paddingTop: '13.5px', // 15.5px in total
      paddingBottom: '28.5px', // 30.5px in total
    },

    '&:after': {
      display: 'none',
    },

    svg: {
      color: 'var(--color-dark-coal)',
      transition: 'transform 0.3s ease',
    },

    ':hover svg': {
      color: 'var(--color-light-red)',
    },

    [`.${buttonClasses.endIcon}`]: {
      marginLeft: '10px',
      width: '30px',
      height: '30px',
    },

    '&.menu-open': {
      [`.${buttonClasses.endIcon} svg`]: {
        transform: 'rotate(180deg)',
      },
    },
  },

  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}));

const DesktopDropdownPopper = styled(Popper)({
  width: 'auto',
  zIndex: 1,
});

const DesktopDropdown = styled(Paper)({
  backgroundColor: 'var(--color-white)',
  boxShadow: 'none',
  border: '1px solid var(--color-medium-light-grey)',
});

const DesktopDropdownMenuList = styled(MenuList)({
  padding: '33px 0',
});

const DesktopDropdownMenuItem = styled(MenuItem)(({ theme }) => ({
  ...theme.typography.body,
  fontWeight: theme.typography.fontWeightRegular,

  padding: '9px 80px 9px 42px !important',
  position: 'relative',

  '&.hidden': {
    display: 'none',
  },

  svg: {
    marginLeft: '10px',
    position: 'absolute',
    right: '33px',
    top: '14px',
  },

  '&:hover, &:focus': {
    color: 'var(--color-light-red)',
    backgroundColor: 'transparent',
  },

  [`&.${menuItemClasses.focusVisible}`]: {
    color: 'var(--color-light-red)',

    '&:hover': {
      backgroundColor: 'var(--color-medium-light-grey)',
    },
  },
}));

// -----------------------------------------------------------------------------
// Native <select> for mobile
// -----------------------------------------------------------------------------

const NativeMobileTextDropdown = styled(FormControl, {
  shouldForwardProp: (prop) => prop !== 'hideBorderBottom',
})<{
  hideBorderBottom?: boolean;
}>(({ theme, hideBorderBottom = false }) => ({
  paddingBottom: '3px',
  paddingTop: '2px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  justifyContent: 'center',
  flexWrap: 'wrap',
  borderBottom: hideBorderBottom
    ? 'none'
    : '1px solid var(--color-medium-light-grey)',
  height: '100%',

  [`.${inputLabelClasses.root}`]: {
    ...theme.typography.body,
    fontWeight: theme.typography.fontWeightMedium,
    color: 'var(--color-dark-coal)',
    position: 'relative',
    display: 'block',
    transform: 'none',
    marginRight: '18px',

    [`&.${inputLabelClasses.focused}`]: {
      color: 'var(--color-dark-coal)',
    },
  },

  [`.${nativeSelectClasses.root}`]: {
    // width: '100%',
  },

  [`.${nativeSelectClasses.icon}`]: {
    width: '30px',
    height: '30px',
  },

  [`.${inputClasses.root}`]: {
    ...theme.typography.body,
    fontWeight: theme.typography.fontWeightRegular,
    marginTop: 0,

    [`.${nativeSelectClasses.select}`]: {
      paddingTop: '4px',
      paddingBottom: '12px',
      paddingLeft: '0',
      textOverflow: 'ellipsis',

      [theme.breakpoints.up('sm')]: {
        paddingTop: '12px',
      },
    },

    [`.${nativeSelectClasses.icon}`]: {
      top: 0,
      color: 'var(--color-dark-coal)',
      width: '30px',
      height: '30px',
    },

    '&:hover': {
      [`& .${nativeSelectClasses.icon}`]: {
        color: 'var(--color-light-red)',
      },
    },
  },

  [theme.breakpoints.up('sm')]: {
    display: 'none',
  },
}));
